import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { AdjustmentFilter } from '@pixi/filter-adjustment';
import { ColorMatrixFilter } from '@pixi/filter-color-matrix';
import { Container, useTick } from '@pixi/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/store';
import { assertUnreachable } from '../../../utils/tsUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { NOT_IN_HAND_FILTER } from './playerFilters';
function createSittingOutOrStandingUpFilter() {
    const filter = new ColorMatrixFilter();
    filter.brightness(0.4, true);
    filter.blackAndWhite(true);
    return filter;
}
const SITTING_OUT_OR_STANDING_UP_FILTER = createSittingOutOrStandingUpFilter();
function createNewPulseFilter() {
    const filter = new AdjustmentFilter();
    filter.gamma = 2; // start at most intense filter
    return filter;
}
export const ActionPlayerFilterContainer = ({ seatIndex, children }) => {
    const isActionSeat = useAppSelector((state) => state.poker.actionSeatIndex === seatIndex);
    const isSeatedPlayerSittingOut = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isSittingOut);
    const isSeatedPlayerStandingUp = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isStandingUp);
    const isSeatedPlayerIn = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isIn);
    const pulseFilter = useRef(createNewPulseFilter());
    const [isAnimating, setIsAnimating] = useState(false);
    const [visibleState, setVisibleState] = useState('in');
    const animate = useCallback((delta) => {
        pulseFilter.current.gamma -= delta * 0.02;
        if (pulseFilter.current.gamma < 1) {
            pulseFilter.current.gamma = 2;
        }
        pulseFilter.current.contrast = pulseFilter.current.gamma;
    }, []);
    // TODO: Move this into a Record:
    const getContainerFilters = useCallback(() => {
        switch (visibleState) {
            case 'in': {
                return null;
            }
            case 'out': {
                return [NOT_IN_HAND_FILTER];
            }
            case 'action': {
                return [pulseFilter.current];
            }
            case 'sittingOut': {
                return [SITTING_OUT_OR_STANDING_UP_FILTER];
            }
            case 'standingUp': {
                return [SITTING_OUT_OR_STANDING_UP_FILTER];
            }
            default: {
                assertUnreachable(visibleState);
            }
        }
    }, [visibleState]);
    useEffect(() => {
        if (isActionSeat) {
            // reset filter to "no effect":
            pulseFilter.current.gamma = 1;
            pulseFilter.current.contrast = 1;
            setVisibleState('action');
            setIsAnimating(true);
        }
        else {
            if (isSeatedPlayerSittingOut) {
                setVisibleState('sittingOut');
            }
            else if (isSeatedPlayerStandingUp) {
                setVisibleState('standingUp');
            }
            else if (isSeatedPlayerIn) {
                setVisibleState('in');
            }
            else {
                setVisibleState('out');
            }
            setIsAnimating(false);
        }
    }, [isActionSeat, animate, isSeatedPlayerSittingOut, isSeatedPlayerStandingUp, isSeatedPlayerIn]);
    useTick(animate, isAnimating);
    const pixiData = isTestEnvironment() ? { type: 'seated-player-visible-state', seatIndex, visibleState } : null;
    return (_jsx(Container, { filters: getContainerFilters(), "pixi-data": pixiData, children: children }));
};
